<template>
    <div>
        <div class="h-12">
            <div class="mb-3 flex gap-4">
                <hts-form-button
                    color="info"
                    icon-name="hi-check-circle-icon"
                    icon-position="right"
                    type="button"
                    :disabled="!itemSelected.length"
                    @click="handleAction('approve')">
                    {{ $t('labels.confirm') }}
                </hts-form-button>

                <hts-form-button
                    color="error"
                    icon-name="hi-minus-circle-icon"
                    icon-position="right"
                    type="button"
                    :disabled="!itemSelected.length"
                    @click="showPopupReject = true">
                    {{ $t('labels.reject') }}
                </hts-form-button>
            </div>
        </div>

        <hts-data-table
            ref="dataTables"
            :headers="headers"
            use-selection
            :server="server"
            :is-row-selectable="isRowSelectable"
            @selection-changed="onSelectionChanged" />

        <hts-modal
            :show="showPopupReject"
            title="Từ chối"
            type="confirm"
            content="Sau khi thực hiện thao tác từ chối, các chặng hàng sẽ biến mất và bạn không thể thực hiện thao tác khác. Bạn có muốn tiếp tục từ chối"
            @hide="showPopupReject = false"
            @confirm="handleAction('reject')" />

        <journey-info
            ref="journeyInfoComponent"
            :editable="false"
            :exclude="['prices']"
            :api="$urlPartner.monitoring.journey.getDetail" />
    </div>
</template>

<script setup lang="ts">
import Route from './-components/route.vue'
import { $api, $lodash, $urlPartner } from '#imports'
import JourneyInfo from '@/pages/trucker-[id]/-components/journey-info.vue'
import CheckboxView from '~/components/data-table/-components/checkbox-view.vue'
import Status from '~/components/status.vue'
import layoutPermissions from '~/data/permissions/layout-permissions'
import Actions from '~/pages/trucker-[id]/driver/ignore/actions.vue'

const { $formatters } = useNuxtApp()
const store = $store()

const cooperator = computed(() => store.system.company.cooperator)

const itemSelected = ref<any[]>([])
const journeyInfoComponent: any = ref()

const server = $urlPartner.trip.journey['get-list']
const showPopupReject = ref(false)
const dataTables = ref()
const isRowSelectable = ref<any>(null)

// const firstDataRerender = (params: any) => {
//   const nodesToSelect: any[] = []
//
//   params.api.forEachNode((node: any) => {
//     nodesToSelect.push(node)
//   }
//
//   params.api.setNodesSelected({ nodes: nodesToSelect, newValue: true })
// }

onMounted(() => {
  isRowSelectable.value = (params: any) => {
    const { status, partnerId } = params.data
    return status === 1 && partnerId === cooperator.value
  }
})

const onSelectionChanged = (data: any) => {
  itemSelected.value = Array.isArray(data?.value) ? $lodash.map(data.value, '_id') : []
}

const showStatus = (data: any) => {
  const { status, partnerId } = data
  if (status === 7) {
    return false
  }

  if (!cooperator.value) {
    return false
  }

  if (partnerId !== cooperator.value) {
    return false
  }

  return true
}

const openView = (data: any) => {
  journeyInfoComponent.value && journeyInfoComponent.value.show(data._id)
}

const headers = ref([
  {
    field: 'code',
    headerName: $t('labels.code'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'orderCode',
    headerName: $t('labels.order'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'journeyStatus',
      label: '',
      keyStatus: 'status',
      check: showStatus
    },
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      {
        name: $t('labels.cancel'),
        value: -1,
        class: { '!bg-error-100': true, '!text-error-800': true } // Màu đỏ (#F44336)
      },
      {
        name: 'Chờ xác nhận',
        value: 1,
        class: { '!bg-yellow-100': true, '!text-yellow-800': true } // Màu vàng (#FFEB3B)
      },
      {
        name: 'Chờ điều đội xe',
        value: 7,
        class: { '!bg-sky-100': true, '!text-sky-800': true } // Màu xanh nhạt (#81D4FA)
      },
      {
        name: 'Chờ điều xe',
        value: 2,
        class: { '!bg-blue-100': true, '!text-blue-800': true } // Màu xanh dương (#2196F3)
      },
      {
        name: 'Có xe dự kiến',
        value: 3,
        class: { '!bg-orange-100': true, '!text-orange-800': true } // Màu cam (#FF9800)
      },
      {
        name: 'Có xe chính thức',
        value: 4,
        class: { '!bg-green-100': true, '!text-green-800': true } // Màu xanh lá cây (#4CAF50)
      },
      {
        name: 'Đang vận chuyển',
        value: 5,
        class: { '!bg-green-200': true, '!text-green-800': true } // Màu xanh lá cây đậm (#388E3C)
      },
      {
        name: 'Kết thúc chặng',
        value: 6,
        class: { '!bg-gray-100': true, '!text-gray-800': true } // Màu xám (#9E9E9E)
      }
    ],
    filterModelDefault: { type: 'equals', filterType: 'number' }
  },
  {
    headerName: 'Thời gian thực hiện chặng hàng',
    valueGetter: (params: any) => {
      const { timeIn, timeOut } = params.data
      return $formatters.datetime(timeIn) + ' - ' + $formatters.datetime(timeOut)
    }
  },
  {
    field: 'journey',
    headerName: $t('labels.route'),
    minWidth: 700,
    cellRenderer: Route,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'vehicleLicensePlate',
    headerName: $t('labels.vehicle'),
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      const vehicleLicensePlate = params.data?.vehicleLicensePlate
      const vehicleTempLicensePlate = params.data?.vehicleTempLicensePlate
      return vehicleLicensePlate || vehicleTempLicensePlate || ''
    }
  },
  {
    field: 'partnerName',
    headerName: $t('labels.fleetVehicle'),
    valueGetter: (params: any) => {
      const { status, partnerId, partnerName } = params.data
      if (partnerId !== cooperator.value) {
        return ''
      }
      return partnerName
    }
  },
  {
    field: 'transportation',
    sortable: true,
    headerName: $t('labels.transportation'),
    valueGetter: (params: any) => {
      const transportation = params?.data.orderData?.transportation
      if (transportation === 'CONTAINER') {
        return $t('labels.containerTruck')
      }
      if (transportation === 'NON_CONTAINER') {
        return $t('labels.nonContainerTruck')
      }
    }
  },
  {
    field: 'contCategory',
    headerName: $t('labels.cont'),
    valueGetter: (params: any) => {
      const contCategory = params.data?.orderData?.contCategory
      if (contCategory) {
        if (contCategory === 20) {
          return $t('labels.20f')
        }
        if (contCategory === 40) {
          return $t('labels.40f')
        }
        if (contCategory === 45) {
          return $t('labels.45f')
        }
      }
    }
  },
  {
    field: 'mergeContainer',
    headerName: 'Yêu cầu đặc biệt',
    valueGetter: (params: any) => {
      const results = [] as string[]
      if (params?.data?.orderData?.mergeContainer) {
        results.push($t('labels.mergeContainer'))
      }

      if (params?.data?.orderData?.headContainer) {
        results.push($t('labels.headContainer'))
      }

      return results.join(', ')
    }
  },

  {
    field: 'statusComfirm',
    headerName: 'Chặng đã bị từ chối',
    cellRendererSelector: (params: any) => {
      const { status, partnerId } = params.data

      if (partnerId !== cooperator.value) {
        return ''
      }

      if (status === 7) {
        return {
          component: CheckboxView,
          params: { checked: true }
        }
      }

      return {
        component: CheckboxView,
        params: { checked: false }
      }
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'tripDataTable',
      storeName: 'Trip',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.view'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.journey_management),
          click: openView
        }
      ]
    }
  }
])

const handleAction = async (action: string) => {
  const ids = itemSelected.value || []
  const api = $urlPartner.trip.journey.responseAllocation
  const data: any = await $api(api, {
    body: {
      ids,
      action
    }
  })
  const resData: any = data.data.value
  if (resData?.success) {
    if (action === 'approve') {
      process.client && $toast().success($t('labels.approveJourneySuccess'))
    } else {
      process.client && $toast().success($t('labels.rejectJourneySuccess'))
      showPopupReject.value = false
    }
    if (dataTables?.value) {
      dataTables.value.refresh()
    }
  }
}
</script>
